import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCalendar, FiCheck } from "react-icons/fi";
import { TbArrowLeft, TbX } from "react-icons/tb";
import { useSnackbar } from "notistack";
import { useCEPData } from "../../../Hooks/CEP";
import {
  birthDateMask,
  birthDateToIso,
  cepMask,
  cpfMask,
  handleBussinessTaxIDInput,
  isValidBirthDate,
  isoToBirthDate,
  phoneMask,
} from "../../../Utils/masks";
import { BeneficialOwnersStep } from "./BeneficialOwnersStep";
import { CompanyDataStep } from "./CompanyDataStep";
import { DocumentUpload } from "./DocumentUpload";
import { LivenessQrStep } from "./LivenessQrStep";
import { AddressFields } from "./AddressFields";
import {
  EMPTY_ADDRESS,
  EMPTY_COMPANY,
  EMPTY_DOCUMENTS,
  EMPTY_LIVENESS,
  EMPTY_PERSONAL,
  PF_STEPS,
  PJ_STEPS,
  buildInitialCompany,
  buildInitialOwners,
  buildInitialPersonal,
  getDocumentDigits,
  isCnpjAccount,
  resolveLivenessSubject,
} from "./utils";

const inputClass =
  "individual-onboarding-input h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20";

const handleDocumentInput = (value, isCompany) => {
  if (isCompany) {
    return handleBussinessTaxIDInput(value);
  }
  return cpfMask(value.replace(/\D/g, "").slice(0, 11));
};

const isValidDocument = (value, isCompany) => {
  const digits = getDocumentDigits(value);
  return isCompany ? digits.length === 14 : digits.length === 11;
};

const validateAddressFields = (address, showWarning) => {
  if (address.zipCode.replace(/\D/g, "").length !== 8) {
    showWarning("Informe um CEP válido.");
    return false;
  }
  if (!address.street.trim() || !address.number.trim() || !address.neighborhood.trim()) {
    showWarning("Preencha rua, número e bairro.");
    return false;
  }
  if (!address.city.trim() || !address.state) {
    showWarning("Informe cidade e estado.");
    return false;
  }
  return true;
};

export const IndividualAccountOnboardingModal = ({
  open,
  onClose,
  accountData = {},
  onSubmit,
  submitting = false,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { fetchCEPData, requestLoading: cepLoading, requestError: cepError } = useCEPData();

  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [personal, setPersonal] = useState(EMPTY_PERSONAL);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [beneficialOwners, setBeneficialOwners] = useState([]);
  const [ownerCepLoading, setOwnerCepLoading] = useState({});
  const [liveness, setLiveness] = useState(EMPTY_LIVENESS);
  const [documentNames, setDocumentNames] = useState({
    addressProof: "",
    socialContract: "",
    sourceOfFunds: "",
  });
  const [documentError, setDocumentError] = useState("");
  const documentsRef = useRef({ ...EMPTY_DOCUMENTS });
  const birthDatePickerRef = useRef(null);
  const cepLookupTimerRef = useRef(null);
  const ownerCepTimersRef = useRef({});

  const companyAccount = isCnpjAccount(accountData);
  const steps = companyAccount ? PJ_STEPS : PF_STEPS;

  const updateDocuments = useCallback((field, file, errorMessage) => {
    if (errorMessage) {
      setDocumentError(errorMessage);
      return;
    }

    if (!file) return;

    documentsRef.current = { ...documentsRef.current, [field]: file };
    setDocumentError("");
    setDocumentNames((current) => ({ ...current, [field]: file.name }));
  }, []);

  const applyCepData = useCallback((setter) => (data) => {
    const street = data?.logradouro || data?.street || "";
    const neighborhood = data?.bairro || data?.neighborhood || "";
    const city = data?.localidade || data?.city || "";
    const state = data?.uf || data?.state || "";

    if (!street && !city) return;

    setter((current) => ({
      ...current,
      street: street || current.street,
      neighborhood: neighborhood || current.neighborhood,
      city: city || current.city,
      state: state || current.state,
    }));
  }, []);

  const handleZipCodeChange = useCallback(
    (value, setter) => {
      const masked = cepMask(value);
      setter((current) => ({ ...current, zipCode: masked }));

      const digits = masked.replace(/\D/g, "");
      if (digits.length !== 8) return;

      if (cepLookupTimerRef.current) {
        clearTimeout(cepLookupTimerRef.current);
      }

      cepLookupTimerRef.current = window.setTimeout(() => {
        fetchCEPData({ cep: digits }).then(applyCepData(setter));
      }, 350);
    },
    [applyCepData, fetchCEPData]
  );

  const handleCompanyZipCodeChange = useCallback(
    (value) => {
      handleZipCodeChange(value, (updater) => {
        setCompany((current) => ({
          ...current,
          address: typeof updater === "function" ? updater(current.address) : updater,
        }));
      });
    },
    [handleZipCodeChange]
  );

  const handleOwnerZipCodeChange = useCallback(
    (ownerIndex, value) => {
      const masked = cepMask(value);

      setBeneficialOwners((current) =>
        current.map((owner, index) =>
          index === ownerIndex ? { ...owner, address: { ...owner.address, zipCode: masked } } : owner
        )
      );

      const digits = masked.replace(/\D/g, "");
      if (digits.length !== 8) return;

      if (ownerCepTimersRef.current[ownerIndex]) {
        clearTimeout(ownerCepTimersRef.current[ownerIndex]);
      }

      ownerCepTimersRef.current[ownerIndex] = window.setTimeout(() => {
        setOwnerCepLoading((current) => ({ ...current, [ownerIndex]: true }));
        fetchCEPData({ cep: digits })
          .then((data) => {
            const street = data?.logradouro || data?.street || "";
            const neighborhood = data?.bairro || data?.neighborhood || "";
            const city = data?.localidade || data?.city || "";
            const state = data?.uf || data?.state || "";

            if (!street && !city) return;

            setBeneficialOwners((current) =>
              current.map((owner, index) =>
                index === ownerIndex
                  ? {
                      ...owner,
                      address: {
                        ...owner.address,
                        street: street || owner.address.street,
                        neighborhood: neighborhood || owner.address.neighborhood,
                        city: city || owner.address.city,
                        state: state || owner.address.state,
                      },
                    }
                  : owner
              )
            );
          })
          .finally(() => {
            setOwnerCepLoading((current) => ({ ...current, [ownerIndex]: false }));
          });
      }, 350);
    },
    [fetchCEPData]
  );

  useEffect(() => {
    if (!open) return;

    setStepIndex(0);
    setCompleted(false);
    setDocumentError("");
    setPersonal(buildInitialPersonal(accountData));
    setAddress({ ...EMPTY_ADDRESS });
    setCompany(buildInitialCompany(accountData));
    setBeneficialOwners(buildInitialOwners(accountData));
    setOwnerCepLoading({});
    setLiveness({ ...EMPTY_LIVENESS });
    documentsRef.current = { ...EMPTY_DOCUMENTS };
    setDocumentNames({
      addressProof: "",
      socialContract: "",
      sourceOfFunds: "",
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    return () => {
      if (cepLookupTimerRef.current) {
        clearTimeout(cepLookupTimerRef.current);
      }
      Object.values(ownerCepTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      ownerCepTimersRef.current = {};
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const currentStep = steps[stepIndex];
  const livenessSubject = resolveLivenessSubject({
    isCompanyAccount: companyAccount,
    personal,
    company,
    beneficialOwners,
  });

  const showWarning = useCallback(
    (message) => {
      enqueueSnackbar(message, { variant: "warning", preventDuplicate: true });
    },
    [enqueueSnackbar]
  );

  const validatePersonal = () => {
    if (!personal.fullName.trim()) {
      showWarning("Informe o nome completo.");
      return false;
    }
    if (!isValidDocument(personal.document, false)) {
      showWarning("Informe um CPF válido.");
      return false;
    }
    if (!isValidBirthDate(personal.birthDate)) {
      showWarning("Informe uma data de nascimento válida (dd/mm/aaaa).");
      return false;
    }
    if (!personal.email.trim()) {
      showWarning("Informe o e-mail.");
      return false;
    }
    if (personal.phone.replace(/\D/g, "").length < 10) {
      showWarning("Informe um telefone válido.");
      return false;
    }
    return true;
  };

  const validateCompany = () => {
    if (!company.businessName.trim()) {
      showWarning("Informe a razão social da empresa.");
      return false;
    }
    if (!isValidDocument(company.cnpj, true)) {
      showWarning("Informe um CNPJ válido.");
      return false;
    }
    return validateAddressFields(company.address, showWarning);
  };

  const validateOwners = () => {
    if (!beneficialOwners.length) {
      showWarning("Adicione pelo menos um beneficiário final.");
      return false;
    }

    if (!beneficialOwners.some((owner) => owner.isControlPerson)) {
      showWarning("Marque pelo menos um beneficiário como pessoa de controle.");
      return false;
    }

    for (let index = 0; index < beneficialOwners.length; index += 1) {
      const owner = beneficialOwners[index];
      const label = `Beneficiário ${index + 1}`;

      if (!owner.firstName.trim() || !owner.lastName.trim()) {
        showWarning(`Informe nome e sobrenome em ${label}.`);
        return false;
      }
      if (getDocumentDigits(owner.cpf).length !== 11) {
        showWarning(`Informe um CPF válido em ${label}.`);
        return false;
      }
      if (!isValidBirthDate(owner.birthDate)) {
        showWarning(`Informe uma data de nascimento válida em ${label}.`);
        return false;
      }
      if (!owner.email.trim()) {
        showWarning(`Informe o e-mail em ${label}.`);
        return false;
      }
      if (owner.phone.replace(/\D/g, "").length < 10) {
        showWarning(`Informe um telefone válido em ${label}.`);
        return false;
      }
      if (!validateAddressFields(owner.address, (message) => showWarning(`${label}: ${message}`))) {
        return false;
      }
    }

    return true;
  };

  const validateAddress = () => validateAddressFields(address, showWarning);

  const validateLiveness = () => {
    if (liveness.status !== "completed") {
      showWarning("Conclua a verificação facial pelo QR Code antes de continuar.");
      return false;
    }
    return true;
  };

  const validateDocuments = () => {
    const docs = documentsRef.current;

    if (!docs.addressProof) {
      setDocumentError("Envie o comprovante de endereço.");
      return false;
    }
    if (companyAccount && !docs.socialContract) {
      setDocumentError("Envie o contrato social da empresa.");
      return false;
    }
    setDocumentError("");
    return true;
  };

  const stepValidators = companyAccount
    ? {
        company: validateCompany,
        owners: validateOwners,
        liveness: validateLiveness,
        documents: validateDocuments,
      }
    : {
        personal: validatePersonal,
        address: validateAddress,
        liveness: validateLiveness,
        documents: validateDocuments,
      };

  const handleNext = () => {
    const validator = stepValidators[currentStep.id];
    if (validator && !validator()) return;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const runAllValidations = () => {
    if (companyAccount) {
      return validateCompany() && validateOwners() && validateLiveness() && validateDocuments();
    }
    return validatePersonal() && validateAddress() && validateLiveness() && validateDocuments();
  };

  const handleSubmit = async () => {
    if (!runAllValidations()) return;

    const success = await onSubmit({
      personal,
      address,
      company,
      beneficialOwners,
      liveness,
      documents: { ...documentsRef.current },
      isCompanyAccount: companyAccount,
    });

    if (success) {
      setCompleted(true);
      onSuccess?.();
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const documentSummary = [
    documentsRef.current.addressProof ? "Comprovante de endereço" : null,
    documentsRef.current.socialContract ? "Contrato social" : null,
    documentsRef.current.sourceOfFunds ? "Origem dos fundos" : null,
  ].filter(Boolean);

  if (!open) return null;

  const isSubmitStep = stepIndex >= steps.length - 1;
  const modalSubtitle = companyAccount
    ? "Preencha os dados da empresa, beneficiários finais, documentação e verificação facial (KYC)."
    : "Preencha seus dados, envie a documentação e conclua a verificação facial (KYC).";

  return createPortal(
    <div className="individual-onboarding-modal" role="dialog" aria-modal="true">
      <button
        type="button"
        className="individual-onboarding-modal__backdrop"
        aria-label="Fechar"
        onClick={handleClose}
      />
      <div className="individual-onboarding-modal__panel">
        {!completed ? (
          <>
            <div className="individual-onboarding-modal__header">
              <div className="relative pr-10">
                <h2 className="text-base font-bold text-brand-dark dark:text-gray-100">
                  Abrir conta individualizada
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{modalSubtitle}</p>
                <button
                  type="button"
                  className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-brand-dark-border dark:hover:text-gray-200"
                  aria-label="Fechar"
                  onClick={handleClose}
                >
                  <TbX className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="individual-onboarding-modal__steps-wrap">
              <div className="individual-onboarding-steps">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === stepIndex;
                  const isDone = index < stepIndex;

                  return (
                    <div
                      key={step.id}
                      className={`individual-onboarding-steps__item ${
                        isActive
                          ? "individual-onboarding-steps__item--active"
                          : isDone
                            ? "individual-onboarding-steps__item--done"
                            : ""
                      }`}
                    >
                      <div className="flex flex-col items-center gap-0.5 text-center">
                        {isDone ? (
                          <FiCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                        ) : (
                          <Icon
                            className={`h-3.5 w-3.5 shrink-0 ${
                              isActive ? "text-[#e7a73f]" : "text-gray-400"
                            }`}
                          />
                        )}
                        <span
                          className={`individual-onboarding-steps__label ${
                            isActive ? "text-brand-dark dark:text-gray-100" : "text-gray-500"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="individual-onboarding-modal__body">
              <div className="space-y-3">
              {currentStep.id === "company" && (
                <CompanyDataStep
                  company={company}
                  onChange={setCompany}
                  onCompanyZipCodeChange={handleCompanyZipCodeChange}
                  cepLoading={cepLoading}
                  cepError={cepError}
                />
              )}

              {currentStep.id === "owners" && (
                <BeneficialOwnersStep
                  owners={beneficialOwners}
                  onChange={setBeneficialOwners}
                  onOwnerZipCodeChange={handleOwnerZipCodeChange}
                  cepLoadingByIndex={ownerCepLoading}
                  cepError={cepError}
                />
              )}

              {currentStep.id === "personal" && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="sm:col-span-2 lg:col-span-3">
                    <span className="mb-1 block text-xs font-medium text-gray-500">
                      Nome completo
                    </span>
                    <input
                      className={inputClass}
                      value={personal.fullName}
                      onChange={(e) =>
                        setPersonal((current) => ({ ...current, fullName: e.target.value }))
                      }
                      required
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-gray-500">CPF</span>
                    <input
                      className={inputClass}
                      value={personal.document}
                      onChange={(e) =>
                        setPersonal((current) => ({
                          ...current,
                          document: handleDocumentInput(e.target.value, false),
                        }))
                      }
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                      autoComplete="off"
                      required
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-gray-500">
                      Data de nascimento
                    </span>
                    <div className="relative">
                      <input
                        type="text"
                        className={`${inputClass} pr-10`}
                        value={personal.birthDate}
                        onChange={(e) =>
                          setPersonal((current) => ({
                            ...current,
                            birthDate: birthDateMask(e.target.value),
                          }))
                        }
                        placeholder="dd/mm/aaaa"
                        inputMode="decimal"
                        autoComplete="off"
                        maxLength={10}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 z-[1] flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-[#e7a73f]"
                        aria-label="Abrir calendário"
                        onClick={() => {
                          const picker = birthDatePickerRef.current;
                          if (!picker) return;
                          if (typeof picker.showPicker === "function") {
                            picker.showPicker();
                            return;
                          }
                          picker.click();
                        }}
                      >
                        <FiCalendar className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      ref={birthDatePickerRef}
                      type="date"
                      className="individual-onboarding-hidden-date"
                      tabIndex={-1}
                      aria-hidden="true"
                      value={birthDateToIso(personal.birthDate)}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setPersonal((current) => ({
                          ...current,
                          birthDate: isoToBirthDate(e.target.value),
                        }));
                      }}
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-gray-500">E-mail</span>
                    <input
                      type="email"
                      className={inputClass}
                      value={personal.email}
                      onChange={(e) =>
                        setPersonal((current) => ({ ...current, email: e.target.value }))
                      }
                      required
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-gray-500">Telefone</span>
                    <input
                      className={inputClass}
                      value={personal.phone}
                      onChange={(e) =>
                        setPersonal((current) => ({
                          ...current,
                          phone: phoneMask(e.target.value),
                        }))
                      }
                      required
                    />
                  </label>
                </div>
              )}

              {currentStep.id === "address" && (
                <AddressFields
                  address={address}
                  onChange={setAddress}
                  onZipCodeChange={(value) => handleZipCodeChange(value, setAddress)}
                  cepLoading={cepLoading}
                  cepError={cepError}
                  idPrefix="personal-address"
                />
              )}

              {currentStep.id === "documents" && (
                <div
                  className="space-y-3"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                    }
                  }}
                >
                  <p className="text-sm text-gray-500">
                    Envie a documentação necessária para análise de compliance. Formatos aceitos:
                    PDF, JPG ou PNG.
                  </p>
                  <div className="individual-onboarding-doc-grid">
                    <DocumentUpload
                      label="Comprovante de endereço"
                      hint="Conta de luz, água ou telefone (últimos 90 dias)"
                      fileName={documentNames.addressProof}
                      onSelect={(selected, errorMessage) =>
                        updateDocuments("addressProof", selected, errorMessage)
                      }
                    />
                    {companyAccount ? (
                      <DocumentUpload
                        label="Contrato social"
                        hint="Documento registrado da empresa (PJ)"
                        fileName={documentNames.socialContract}
                        onSelect={(selected, errorMessage) =>
                          updateDocuments("socialContract", selected, errorMessage)
                        }
                      />
                    ) : null}
                    <DocumentUpload
                      label="Origem dos fundos"
                      hint="Extrato, IR ou documento complementar"
                      fileName={documentNames.sourceOfFunds}
                      onSelect={(selected, errorMessage) =>
                        updateDocuments("sourceOfFunds", selected, errorMessage)
                      }
                      optional
                    />
                  </div>
                  {documentError ? (
                    <p className="text-sm font-medium text-red-500">{documentError}</p>
                  ) : null}
                </div>
              )}

              {currentStep.id === "liveness" && (
                <LivenessQrStep
                  personal={livenessSubject}
                  liveness={liveness}
                  onChange={setLiveness}
                  onError={(message) => enqueueSnackbar(message, { variant: "info" })}
                />
              )}

              {currentStep.id === "review" && companyAccount && (
                <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-300">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Empresa
                    </p>
                    <p className="mt-1 font-medium text-brand-dark">{company.businessName}</p>
                    <p>{company.cnpj}</p>
                    <p className="mt-1">
                      {company.address.street}, {company.address.number}
                      {company.address.complement ? ` - ${company.address.complement}` : ""}
                    </p>
                    <p>
                      {company.address.neighborhood} · {company.address.city}/{company.address.state}
                    </p>
                    <p>CEP {company.address.zipCode}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Beneficiários finais
                    </p>
                    <ul className="mt-2 space-y-2">
                      {beneficialOwners.map((owner, index) => (
                        <li key={`review-owner-${index}`} className="rounded-lg bg-white px-3 py-2">
                          <p className="font-medium text-brand-dark">
                            {owner.firstName} {owner.lastName}
                            {owner.isControlPerson ? " · Pessoa de controle" : ""}
                          </p>
                          <p>
                            {owner.cpf} · {owner.email}
                          </p>
                          <p>{owner.phone}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Documentação
                    </p>
                    <p className="mt-1">{documentSummary.join(" · ") || "Nenhum documento"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Verificação facial
                    </p>
                    <p className="mt-1 text-emerald-700">Liveness concluído com sucesso.</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ao confirmar, sua solicitação será enviada para análise de compliance (KYC).
                  </p>
                </div>
              )}

              {currentStep.id === "review" && !companyAccount && (
                <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-300">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Dados pessoais
                    </p>
                    <p className="mt-1 font-medium text-brand-dark">{personal.fullName}</p>
                    <p>
                      {personal.document} · {personal.email}
                    </p>
                    <p>{personal.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Endereço
                    </p>
                    <p className="mt-1">
                      {address.street}, {address.number}
                      {address.complement ? ` - ${address.complement}` : ""}
                    </p>
                    <p>
                      {address.neighborhood} · {address.city}/{address.state}
                    </p>
                    <p>CEP {address.zipCode}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Documentação
                    </p>
                    <p className="mt-1">{documentSummary.join(" · ") || "Nenhum documento"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Verificação facial
                    </p>
                    <p className="mt-1 text-emerald-700">Liveness concluído com sucesso.</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ao confirmar, sua solicitação será enviada para análise de compliance (KYC).
                  </p>
                </div>
              )}
              </div>
            </div>

            <div className="individual-onboarding-modal__footer">
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  className="btn-back w-full sm:w-auto"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  <TbArrowLeft className="h-4 w-4" />
                  Voltar
                </button>
              ) : (
                <span className="hidden sm:block" />
              )}

              {!isSubmitStep ? (
                <button
                  type="button"
                  className="btn-primary w-full sm:ml-auto sm:w-auto"
                  onClick={handleNext}
                >
                  Continuar
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary w-full sm:ml-auto sm:w-auto"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Enviando…" : "Enviar solicitação"}
                </button>
              )}
              </div>
            </div>
          </>
        ) : (
          <div className="individual-onboarding-modal__success text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <FiCheck className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-bold text-brand-dark">Solicitação enviada</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              Recebemos seus dados, documentação e verificação facial. Sua conta individualizada
              será liberada após a conclusão da análise KYC.
            </p>
            <button type="button" className="btn-primary mt-6" onClick={handleClose}>
              Entendi
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
